import React, { useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import PropTypes from "prop-types";
import { AppContext } from "../../../context";
import { BRANDS, MODES } from "../../../configs";
import getDestinations from "./utils/getDestinations";
import getHotels from "./utils/getHotels";
import getInitialDestinationOrHotel from "./utils/getInitialDestinationOrHotel";
import { TypeaheadListViewInput } from "../../../components/TypeaheadListViewInput";
import { ListView } from "./ListView";
import {
	toFieldDestinationsAdapter,
	toFieldHotelsAdapter
} from "./ToField.adapter";
import { PACKAGES_TO_FIELD_ID } from "../../../configs/const";
import { useInputSearchContext } from "../../../context/app/searchValuesContext";

import styles from "./List.scss";

const ToField = ({
	className,
	mode,

	error,
	isOnlyToField,
	stopPropagationOnSOVMobile,

	selectedTo,
	selectedFrom,
	previousSessionValue,

	onChange,
	resetError,
	handleFocus,
	setAllDestinations,
	setNextButtonDisabled,
	setShowPassengerInput,
	setSovMobileIsFromInputEmpty,
	handleSovMobileInputSelectionToView
}) => {
	const {
		values: { toFieldData },
		storeSearchValues
	} = useInputSearchContext();
	const [useAppState] = AppContext.useAppContext();
	const isSovBrand =
		useAppState.brand.toLowerCase() === BRANDS.SOV.toLowerCase();
	const [typeaheadData, setTypeaheadData] = useState(
		isSovBrand && useAppState.sovSimplifiedSearchBar ? toFieldData : []
	);
	const [hotelsList, setHotelsList] = useState({});
	const [listViewData, setListViewData] = useState([]);
	const [initialGatewayOrHotel, setInitialGatewayOrHotel] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isHasSelectedItem, setIsHasSelectedItem] = useState(false);

	useDeepCompareEffect(() => {
		if (stopPropagationOnSOVMobile && isSovBrand) {
			setIsLoading(false);
		} else if (selectedFrom) {
			onChange(null);
			setIsLoading(true);

			if (previousSessionValue?.label || selectedTo?.label) {
				setIsHasSelectedItem(true);
			}

			getDestinations(
				useAppState.lang,
				useAppState.alias,
				selectedFrom.value,
				useAppState.brand
			)
				.then(data =>
					toFieldDestinationsAdapter(
						data,
						{
							destinations: useAppState.getDictionary("destinations"),
							southernVacations: useAppState.getDictionary(
								"southern-vacations"
							),
							USAVacations: useAppState.getDictionary("usa-vacations"),
							canadaVacations: useAppState.getDictionary("canada-vacations"),
							canadaUSAVacations: useAppState.getDictionary(
								"canada-usa-vacations"
							),
							europeanVacations: useAppState.getDictionary(
								"european-vacations"
							),
							allDestinations: useAppState.getDictionary("all-destinations"),
							otherVacations: useAppState.getDictionary("other-vacations"),
							allSouth: useAppState.getDictionary("all-south"),
							allUS: useAppState.getDictionary("all-united-states"),
							allEurope: useAppState.getDictionary("all-europe"),
							allCanada: useAppState.getDictionary("all-canada")
						},
						useAppState.brand.toLowerCase() === BRANDS.VWQ.toLowerCase()
							? { region: [styles.region, styles.vwq] }
							: { region: styles.region },
						useAppState.brand
					)
				)
				.then(destinations => {
					const [baseDestination, list, listView] = destinations;

					if (isSovBrand) {
						setTypeaheadData(list);
						setIsLoading(false);

						storeSearchValues({ type: "toFieldData", toFieldData: list });
					} else {
						getHotels(
							useAppState.lang,
							selectedFrom.value,
							baseDestination?.value,
							useAppState.brand
						)
							.then(data => {
								const hotels = toFieldHotelsAdapter(
									data,
									useAppState.getDictionary("hotels")
								);
								setAllDestinations(baseDestination?.value);
								setHotelsList(hotels);
								setTypeaheadData(list);
								setListViewData(listView);

								// Set initial gateway based on the gateway list + hotel list
								setInitialGatewayOrHotel(
									getInitialDestinationOrHotel(
										list?.[0]?.items ?? [],
										hotels?.items ?? [],
										previousSessionValue
									)
								);
							})
							.catch(err => {
								setHotelsList(null);
								setAllDestinations(baseDestination?.value);
								setTypeaheadData(list);
								setListViewData(listView);

								// Since getHotel failed - set initial gateway based on just the gateway list
								setInitialGatewayOrHotel(
									getInitialDestinationOrHotel(
										list?.[0]?.items ?? [],
										[],
										previousSessionValue
									)
								);

								console.error(err);
							})
							.finally(() => {
								setIsLoading(false);
							});
					}
				})
				.catch(err => console.error(err));
		}
	}, [
		useAppState,
		selectedFrom,
		previousSessionValue,
		onChange,
		stopPropagationOnSOVMobile
	]);

	const onTypeaheadInputChange = newValue => {
		if (newValue?.length > 2 && hotelsList) {
			setTypeaheadData([typeaheadData[0], hotelsList]);
		} else {
			setTypeaheadData([typeaheadData[0]]);
		}
	};

	const placeHolderAndLabel = isOnlyToField
		? useAppState.getDictionary("search-destinations")
		: useAppState.getDictionary("going-to");

	const placeHolderByBrand = isSovBrand
		? placeHolderAndLabel
		: useAppState.getDictionary("destination-or-hotel");

	return (
		<TypeaheadListViewInput
			id={PACKAGES_TO_FIELD_ID}
			mode={mode}
			brand={useAppState.brand}
			className={className}
			label={isSovBrand ? placeHolderAndLabel : useAppState.getDictionary("to")}
			helperText={
				useAppState.sovSimplifiedSearchBar
					? useAppState.getDictionary("enter-a-destination")
					: useAppState.getDictionary("select-a-destination")
			}
			error={error}
			isLoading={isLoading}
			isOnlyToField={isOnlyToField}
			isHasSelectedItem={isHasSelectedItem}
			data={typeaheadData}
			selectedItem={selectedTo}
			initialItem={initialGatewayOrHotel}
			placeholder={
				useAppState.brand.toLowerCase() !== BRANDS.VWQ.toLowerCase()
					? placeHolderByBrand
					: null
			}
			reactDOM={useAppState.reactDOM}
			appRoot={useAppState.appRoot}
			onChange={onChange}
			resetError={resetError}
			setIsHasSelectedItem={setIsHasSelectedItem}
			onInput={onTypeaheadInputChange}
			setNextButtonDisabled={setNextButtonDisabled}
			setShowPassengerInput={setShowPassengerInput}
			setSovMobileIsFromInputEmpty={setSovMobileIsFromInputEmpty}
			handleFocus={handleFocus}
			handleSovMobileInputSelectionToView={handleSovMobileInputSelectionToView}
			labels={{
				noResults: useAppState.getDictionary("no-results-found"),
				list: useAppState.getDictionary("list")
			}}>
			{useAppState.brand.toLowerCase() !== BRANDS.SOV.toLowerCase() && (
				<ListView
					brand={useAppState.brand}
					data={listViewData}
					selectedItem={selectedTo}
					onChange={onChange}
				/>
			)}
		</TypeaheadListViewInput>
	);
};

ToField.propTypes = {
	className: PropTypes.string,
	mode: PropTypes.string,

	error: PropTypes.bool,
	isOnlyToField: PropTypes.bool,
	stopPropagationOnSOVMobile: PropTypes.bool,

	previousSessionValue: PropTypes.shape({
		isHotel: PropTypes.bool,
		label: PropTypes.string,
		value: PropTypes.string
	}),
	selectedFrom: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	selectedTo: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),

	resetError: PropTypes.func,
	setAllDestinations: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setNextButtonDisabled: PropTypes.func,
	setShowPassengerInput: PropTypes.func,
	setSovMobileIsFromInputEmpty: PropTypes.func,
	handleFocus: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func
};

ToField.defaultProps = {
	className: undefined,
	mode: MODES.SEARCH,

	error: false,
	isOnlyToField: false,
	stopPropagationOnSOVMobile: false,

	previousSessionValue: undefined,
	selectedFrom: undefined,
	selectedTo: undefined,

	resetError: () => {},
	setAllDestinations: () => {},
	setNextButtonDisabled: () => {},
	setShowPassengerInput: () => {},
	setSovMobileIsFromInputEmpty: () => {},
	handleFocus: () => {},
	handleSovMobileInputSelectionToView: () => {}
};

export default ToField;
export { ToField };
